import React, { useState } from "react";
import { PawPrint, Send, Star } from "lucide-react";

/**
 * Quick-tap park review.
 *
 * Three required structured questions + an optional 280-char comment,
 * designed to take ~10 seconds. Replaces the old free-text +
 * star-rating combo while remaining backward-compatible — old reviews
 * (rating + text only) still render via ReviewCard, and the new
 * structured fields are nullable on the backend.
 *
 * Props:
 *   onSubmit({rating, cleanliness, crowdedness, would_return, text}) -> Promise
 *   submitting: bool
 *
 * The component owns its form state and resets after a successful
 * submit (parent calls setSubmitting(false) when the request resolves).
 */

const QUESTIONS = [
  {
    key: "cleanliness",
    label: "How clean was it?",
    options: [
      { value: "spotless", label: "Spotless" },
      { value: "clean_enough", label: "Clean enough" },
      { value: "needs_work", label: "Needs work" },
    ],
  },
  {
    key: "crowdedness",
    label: "How crowded was it?",
    options: [
      { value: "empty", label: "Empty" },
      { value: "just_right", label: "Just right" },
      { value: "too_crowded", label: "Too crowded" },
    ],
  },
  {
    key: "would_return",
    label: "Would you go back?",
    options: [
      { value: "definitely", label: "Definitely" },
      { value: "maybe", label: "Maybe" },
      { value: "nope", label: "Nope" },
    ],
  },
];

const MAX_COMMENT = 280;

// Paw row used both inline in the form (interactive) and read-only on
// posted reviews. Filled paws use #4A7C59, empty paws outline in
// #D4C9B0 per the brand spec.
export const PawRating = ({
  value,
  onChange,
  size = "w-9 h-9",
  testIdPrefix = "paw",
}) => {
  const readOnly = typeof onChange !== "function";
  return (
    <div
      className="flex items-center gap-1"
      role={readOnly ? "img" : "radiogroup"}
      aria-label={`Paw rating: ${value} of 5`}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        const Tag = readOnly ? "span" : "button";
        const tagProps = readOnly
          ? {}
          : {
              type: "button",
              onClick: () => onChange(n),
              "data-testid": `${testIdPrefix}-${n}`,
              "aria-label": `${n} paw${n === 1 ? "" : "s"}`,
              "aria-pressed": filled,
            };
        return (
          <Tag
            key={n}
            {...tagProps}
            className={readOnly ? "inline-flex" : "p-0.5 transition-transform active:scale-90"}
          >
            <PawPrint
              className={size}
              style={{
                color: filled ? "#4A7C59" : "#D4C9B0",
                fill: filled ? "#4A7C59" : "transparent",
                strokeWidth: 2,
              }}
            />
          </Tag>
        );
      })}
    </div>
  );
};

// One pill-shaped answer chip. Cream + tan border off, brand-green
// with white text on. Wide tap target — 44×40 minimum on mobile.
const AnswerChip = ({ selected, onClick, label, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    aria-pressed={selected}
    className="px-4 py-2 rounded-full text-sm font-body font-semibold transition-all active:scale-95 border whitespace-nowrap"
    style={
      selected
        ? {
            backgroundColor: "#4A7C59",
            color: "#FFFFFF",
            borderColor: "#4A7C59",
          }
        : {
            backgroundColor: "#F5F0E8",
            color: "#1A1A0F",
            borderColor: "#D4C9B0",
          }
    }
  >
    {label}
  </button>
);

// Place types that get the service-business sub-rating block
// (price/staff/wait/would_return). Keep this in sync with the JSON-LD
// `additionalProperty` gate in ParkDetail.jsx — both have to agree on
// which categories store and display these fields.
const SERVICE_PLACE_TYPES = new Set(["vet", "groomer", "boarding", "trainer"]);

// Reusable star-rating row for the service-business dimensions. Same
// pattern as PawRating below but rendered as filled-star icons because
// the spec calls for stars specifically on these sub-ratings (paw
// icons remain for the overall dog-friendliness rating).
const SubRatingStars = ({ label, value, onChange, testIdPrefix }) => (
  <div>
    <div className="font-body text-sm font-semibold" style={{ color: "#1A1A0F" }}>
      {label}
    </div>
    <div className="mt-1.5 flex items-center gap-1" role="radiogroup" aria-label={`${label}: ${value || 0} of 5`}>
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = n <= value;
        return (
          <button
            key={n}
            type="button"
            data-testid={`${testIdPrefix}-${n}`}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            aria-pressed={filled}
            onClick={() => onChange(n)}
            className="p-0.5 transition-transform active:scale-90"
          >
            <Star
              className="w-7 h-7"
              style={{
                color: filled ? "#4A7C59" : "#D4C9B0",
                fill: filled ? "#4A7C59" : "transparent",
                strokeWidth: 2,
              }}
            />
          </button>
        );
      })}
    </div>
  </div>
);

const QuickReviewForm = ({ onSubmit, submitting, placeType }) => {
  const isService = SERVICE_PLACE_TYPES.has(placeType);
  const [rating, setRating] = useState(0);
  const [answers, setAnswers] = useState({
    cleanliness: null,
    crowdedness: null,
    would_return: null,
  });
  // Service-category sub-ratings live in their own state slice so the
  // happy path for park/beach/restaurant/trail doesn't have to track
  // fields it never uses.
  const [subRatings, setSubRatings] = useState({
    price_rating: null,
    staff_rating: null,
    wait_rating: null,
  });
  // Service categories use a separate "Would you go back?" pill row
  // distinct from the existing `answers.would_return` (which is part
  // of the park-style cleanliness/crowdedness/would_return triplet).
  // Stored in its own state to avoid coupling the two flows.
  const [serviceWouldReturn, setServiceWouldReturn] = useState(null);
  const [text, setText] = useState("");

  const allAnswered = isService
    // Service categories: overall rating only — sub-ratings &
    // would-return are optional per spec. ("Send null for any
    // sub-rating the user skipped".)
    ? rating >= 1
    : (
        rating >= 1 &&
        answers.cleanliness &&
        answers.crowdedness &&
        answers.would_return
      );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allAnswered || submitting) return;
    // Common payload — `onSubmit` accepts every key; the backend
    // model has all of them as optional so unused ones are dropped
    // server-side as None.
    const payload = isService
      ? {
          rating,
          // Park-style fields explicitly nulled so the API doesn't
          // see leftover state from a category-switch (shouldn't
          // happen in practice but keeps the contract clean).
          cleanliness: null,
          crowdedness: null,
          would_return: serviceWouldReturn,
          price_rating: subRatings.price_rating,
          staff_rating: subRatings.staff_rating,
          wait_rating: subRatings.wait_rating,
          text: text.trim(),
        }
      : {
          rating,
          cleanliness: answers.cleanliness,
          crowdedness: answers.crowdedness,
          would_return: answers.would_return,
          text: text.trim(),
        };
    await onSubmit(payload);
    // Reset on success — parent will toggle submitting back to false
    // after the request resolves. We reset eagerly so the form looks
    // fresh while the success toast is still on screen.
    setRating(0);
    setAnswers({ cleanliness: null, crowdedness: null, would_return: null });
    setSubRatings({ price_rating: null, staff_rating: null, wait_rating: null });
    setServiceWouldReturn(null);
    setText("");
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="review-form"
      className="mt-5 rounded-2xl p-4 sm:p-5 border"
      style={{ backgroundColor: "#F5F0E8", borderColor: "#D4C9B0" }}
    >
      {/* Step 1 — Paw rating */}
      <div>
        <div
          className="font-body text-sm font-semibold"
          style={{ color: "#1A1A0F" }}
        >
          How dog-friendly is this place?
        </div>
        <div className="mt-2">
          <PawRating value={rating} onChange={setRating} testIdPrefix="review-paw" />
        </div>
      </div>

      {/* Step 2 — Quick-tap questions.
          Park-style categories (park/beach/restaurant/trail) see the
          existing cleanliness/crowdedness/would_return triplet.
          Service categories (vet/groomer/boarding/trainer) get a
          different block: three 5-star sub-ratings (price, staff,
          wait) plus a "Would you go back?" pill row. */}
      {isService ? (
        <div className="mt-5 space-y-4" data-testid="service-subratings">
          <SubRatingStars
            label="Price / Value"
            value={subRatings.price_rating || 0}
            onChange={(n) => setSubRatings((s) => ({ ...s, price_rating: n }))}
            testIdPrefix="subrating-price"
          />
          <SubRatingStars
            label="Staff & Care"
            value={subRatings.staff_rating || 0}
            onChange={(n) => setSubRatings((s) => ({ ...s, staff_rating: n }))}
            testIdPrefix="subrating-staff"
          />
          <SubRatingStars
            label="Wait Time"
            value={subRatings.wait_rating || 0}
            onChange={(n) => setSubRatings((s) => ({ ...s, wait_rating: n }))}
            testIdPrefix="subrating-wait"
          />
          <div>
            <div className="font-body text-sm font-semibold" style={{ color: "#1A1A0F" }}>
              Would you go back?
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { value: "definitely", label: "Definitely 👍" },
                { value: "maybe",      label: "Maybe 🤔" },
                { value: "nope",       label: "Nope 👎" },
              ].map((opt) => (
                <AnswerChip
                  key={opt.value}
                  selected={serviceWouldReturn === opt.value}
                  onClick={() => setServiceWouldReturn(opt.value)}
                  label={opt.label}
                  testId={`service-would-return-${opt.value}`}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {QUESTIONS.map((q) => (
            <div key={q.key}>
              <div
                className="font-body text-sm font-semibold"
                style={{ color: "#1A1A0F" }}
              >
                {q.label}
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {q.options.map((opt) => (
                  <AnswerChip
                    key={opt.value}
                    selected={answers[q.key] === opt.value}
                    onClick={() =>
                      setAnswers((cur) => ({ ...cur, [q.key]: opt.value }))
                    }
                    label={opt.label}
                    testId={`review-${q.key}-${opt.value}`}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Step 3 — Optional comment */}
      <div className="mt-5">
        <textarea
          data-testid="review-text"
          value={text}
          onChange={(e) =>
            setText(e.target.value.slice(0, MAX_COMMENT))
          }
          placeholder="Anything else dog parents should know? (optional)"
          maxLength={MAX_COMMENT}
          rows={3}
          className="w-full bg-white border rounded-2xl p-3 text-sm font-body resize-none focus:outline-none focus:ring-2"
          style={{
            borderColor: "#D4C9B0",
            color: "#1A1A0F",
          }}
        />
        <div
          className="mt-1 text-xs text-right font-body"
          style={{ color: "#1A1A0F", opacity: 0.5 }}
        >
          {text.length}/{MAX_COMMENT}
        </div>
      </div>

      <div className="flex items-center justify-end mt-2">
        <button
          type="submit"
          data-testid="review-submit"
          disabled={!allAnswered || submitting}
          className="font-body font-semibold rounded-full px-5 py-2 transition-all active:scale-95 disabled:opacity-50 text-sm flex items-center gap-1.5"
          style={{
            backgroundColor: "#4A7C59",
            color: "#FFFFFF",
          }}
        >
          <Send className="w-4 h-4" />
          Post Review
        </button>
      </div>
    </form>
  );
};

export default QuickReviewForm;
